import React, {FC, useCallback, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import cn from 'classnames';
import {useLocalizer} from '@/common/services/localizer';
import {LocationPoint} from '@/common/interfaces/location';
import {setDefaultLocationConfigShown} from '@/app/store/ui/actions';
import {LocationLink} from '@/app/components/common/LocationLink';
import styles from './SearchSuggests.css';

interface SearchSuggestsProps {
	suggests: (LocationPoint & {userDefault?: 'determined' | 'defined'})[];
	focusedIndex: number;
	onFocusRequire(index: number): void;
	onBeforeNavigate(): void;
}

const scrollTo = (element: HTMLElement, offset: number) => {
	if (element.scrollTo) {
		element.scrollTo({top: offset, behavior: 'smooth'});
	} else {
		element.scrollTop = offset;
	}
};

export const SearchSuggests: FC<SearchSuggestsProps> = props => {
	const {suggests, focusedIndex, onFocusRequire, onBeforeNavigate} = props;

	const t = useLocalizer();
	const rootRef = useRef<HTMLMenuElement>(null);
	const focusedItemRef = useRef<HTMLElement>(null);

	const dispatch = useDispatch();

	const handlePointerDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
	}, []);

	const handleUsersDefaultChange = () => {
		dispatch(setDefaultLocationConfigShown(true));
		onBeforeNavigate();
	};

	const handleItemHover = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			const index = Number(event.currentTarget.dataset.index);
			onFocusRequire(index);
		},
		[onFocusRequire],
	);

	// Scroll into the focused item when it is outside of the scrollable viewport
	useEffect(() => {
		if (focusedIndex === -1 || !rootRef.current || !focusedItemRef.current) {
			return;
		}
		const rootHeight = rootRef.current.clientHeight;
		const rootScroll = rootRef.current.scrollTop;
		const itemOffset = focusedItemRef.current.offsetTop;

		if (itemOffset - rootScroll > rootHeight || itemOffset < rootScroll) {
			scrollTo(rootRef.current, itemOffset - 200);
		}
	}, [focusedIndex]);

	return (
		<menu ref={rootRef} className={styles.root} onMouseDown={handlePointerDown}>
			{suggests.length === 0 && <p className={styles.notFoundPlaceholder}>{t('searchNotFound')}</p>}
			{suggests.map((location, index) =>
				location.userDefault ? (
					<div
						key=':user-default'
						className={cn(styles.defaultItem, index === focusedIndex && styles.isFocused)}>
						<LocationLink
							ref={index === focusedIndex ? focusedItemRef : undefined}
							className={cn(
								styles.defaultLink,
								location.userDefault === 'defined' && styles.isUsersDefault,
							)}
							location={location}
							data-index={index}
							onMouseOver={handleItemHover}
							onClick={onBeforeNavigate}>
							<div className={styles.defaultMain}>
								{location.title}, <span className={styles.description}>{location.description}</span>
								<p className={styles.defaultLegend}>
									{t(
										location.userDefault === 'determined'
											? 'defaultLocationDeterminedHint'
											: 'defaultLocationDefinedHint',
									)}
								</p>
							</div>
						</LocationLink>
						<button className={styles.editButton} type='button' onClick={handleUsersDefaultChange}>
							{t('change')}
						</button>
					</div>
				) : (
					<LocationLink
						ref={index === focusedIndex ? focusedItemRef : undefined}
						key={location.id}
						className={styles.item}
						location={location}
						data-index={index}
						onMouseOver={handleItemHover}
						onClick={onBeforeNavigate}>
						<span className={cn(styles.itemContent, index === focusedIndex && styles.isFocused)}>
							{location.title}
							{location.description && (
								<>
									, <span className={styles.description}>{location.description}</span>
								</>
							)}
						</span>
					</LocationLink>
				),
			)}
		</menu>
	);
};

SearchSuggests.displayName = 'SearchSuggests';
